import dayjs from 'dayjs';
import { v4 } from 'uuid';

import { selectedBorerIdKey } from '@/mobx-models/Equipment/selectedBorerIdKey';

import { PopulatedLocation } from '../rxdb/Locations/useLocations';
import { YesNo } from './enums';

export const jsonMapReplacer = (key: string, value: any) => {
  if (value instanceof Map) {
    return {
      dataType: 'Map',
      value: Array.from(value.entries()), // or with spread: value: [...value]
    };
  }
  return value;
};

export const jsonMapReviver = (key: string, value: any) => {
  if (typeof value === 'object' && value !== null) {
    if (value.dataType === 'Map') {
      return new Map(value.value);
    }
  }
  return value;
};

export const dayJsToMinuteUnix = (timeString: string) =>
  dayjs(timeString).set('seconds', 0).set('milliseconds', 0).unix();

export const getBorerId = (): string => {
  // Get the borers id from local storage
  const borerInfo = localStorage.getItem(selectedBorerIdKey);
  let borerId = '';
  if (borerInfo) {
    const borerInfoObj = JSON.parse(borerInfo);
    borerId = borerInfoObj.id;
  }

  return borerId;
};

export const convertListOfFilesAndFileListsToArray = (files: File[] | FileList[]) => {
  let filesArray: File[] = [];

  if (files !== null) {
    [...files].forEach(value => {
      if (value instanceof FileList) {
        let numberOfFiles = value.length;
        while (numberOfFiles--) {
          filesArray = [...filesArray, value[numberOfFiles]];
        }
      } else {
        filesArray = [...filesArray, value];
      }
    });
  }

  return filesArray;
};

export const formatFileNamesToBeUnique = (files: File[], prefix = 'image', usePrefix = false) => {
  const unixString = dayjs().unix().toString();
  return files.map(file => {
    const fileBlob = file.slice(0, file.size, file.type) as BlobPart;
    const fileName = !usePrefix ? `${prefix}-${unixString}-${file?.name}` : `${file?.name}`;
    const updatedFile = new File([fileBlob], `${fileName}` as string, {
      type: file?.type,
    });

    return updatedFile;
  });
};

export const generateFormattedImageName = (
  imagePrefix: string,
  extension: string,
  currentDate: string,
): string => {
  const fileNamePrefix = imagePrefix.split('/').join('-');
  const uuid = v4();
  // The six rightmost characters of a UUID are generally the most variable.
  const suffix = uuid.slice(0, 6);
  const fileName = `${fileNamePrefix}${currentDate}_${suffix}.${extension}`;
  return fileName;
};

export const getFormattedImages = (imagePrefix: string, selectedImages: File[] | FileList[]) => {
  let formattedFiles: File[] = [];

  const date = new Date();
  const currentDate = `${date.getFullYear()}${`0${date.getMonth() + 1}`.slice(
    -2,
  )}${`0${date.getDate()}`.slice(-2)}`;

  if (!selectedImages) return formattedFiles;

  convertListOfFilesAndFileListsToArray(selectedImages).forEach(value => {
    let file: File;

    if (value instanceof FileList) {
      file = value[0];
    } else {
      file = value;
    }

    const fileBlob = file?.slice(0, file.size, file.type) as BlobPart;
    const extension = file?.type.split('/')[1];
    const fileName = generateFormattedImageName(imagePrefix, extension, currentDate);
    const updatedFile = new File([fileBlob], fileName as string, {
      type: file?.type,
    });
    formattedFiles = [...formattedFiles, updatedFile];
  });

  return formattedFiles;
};

export const formatAsStartCase = (str?: string) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getSurveyPointInt = (surveyPointDescription: string) => {
  // Vanscoy survey points have 'S-' prefix
  if (surveyPointDescription.toUpperCase().substring(0, 2) === 'S-') {
    return parseInt(surveyPointDescription.substring(2));
  }
  return parseInt(surveyPointDescription);
};

export const getTimeUntilNextMinute = () =>
  60 * 1000 - ((new Date().getTime() % (60 * 1000)) - 100);

export const locationDifferences = (a: PopulatedLocation, b: PopulatedLocation) => {
  return (
    a.panel?.id !== b.panel?.id ||
    a.pass?.id !== b.pass?.id ||
    a.sequence?.id !== b.sequence?.id ||
    a.surveyPoint?.id !== b.surveyPoint?.id ||
    a.block?.id !== b.block?.id ||
    a.room?.id !== b.room?.id
  );
};

export const sleep = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const capitalizeFirstLetter = (str: string) => {
  if (str) return str.charAt(0).toUpperCase() + str.slice(1);

  return undefined;
};

export const capitalizeFirstLetterOnly = (str: string) => {
  if (str) return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  return undefined;
};

export const toCamelCase = (sentence: string) => {
  let out = '';
  sentence.split(' ').forEach((el, idx) => {
    const add = el.toLowerCase();
    out += idx === 0 ? add : add[0].toUpperCase() + add.slice(1);
  });
  return out;
};

export const toKebabCase = (sentence: string) => {
  let out = '';
  sentence.split(' ').forEach((el, idx) => {
    out += `${el.toLowerCase()}${idx === sentence.split(' ').length - 1 ? '' : '-'}`;
  });
  return out;
};

export const compareObjects = (obj1, obj2) => {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
    return false;
  }

  const keysA = Object.keys(obj1);
  const keysB = Object.keys(obj2);

  if (keysA.length !== keysB.length) {
    return false;
  }

  let result = true;

  keysA.forEach(key => {
    if (!keysB.includes(key)) {
      result = false;
    }

    if (typeof obj1[key] === 'function' || typeof obj2[key] === 'function') {
      if (obj1[key].toString() !== obj2[key].toString()) {
        result = false;
      }
    }

    if (!compareObjects(obj1[key], obj2[key])) {
      result = false;
    }
  });

  return result;
};

export const numberOrNull = (value: string | number | null) => {
  if (value === null) return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num;
};

export const addLeadingSpaceToStringIfItExists = (str: string | undefined) => {
  if (str && str.length > 0) {
    return ` ${str}`;
  }
  return undefined;
};

export const booleanToYesNo = (value: boolean | undefined | null) => {
  if (value === undefined || value === null) {
    return '';
  }
  return value ? YesNo.YES : YesNo.NO;
};

export const toLowerCaseWithDashes = (value: string) => {
  return value.toLowerCase().replace(/\s+/g, '-');
};

export const trimDescriptionForFileName = (
  description: string | undefined | null,
  maxLength = 10,
): string => {
  if (!description) {
    return '';
  }
  return description.length > maxLength ? description.slice(0, maxLength) : description;
};
